// Static marketing content for the Astria landing page.
//
// Seeded from the approved landing design (AstriaFinal/public/index.html) and
// maintained here by hand. English strings double as translation keys (see
// landingTranslations.ts), so editing one means updating its row there too.

export type LandingLangCode =
  | "en" | "th" | "vi" | "id" | "ms" | "fil" | "ko" | "ja"
  | "zh-CN" | "hi" | "ar" | "es" | "pt-BR" | "fr" | "de";

export interface LandingLanguage {
  code: LandingLangCode;
  nativeName: string;
  englishName: string;
  dir: "ltr" | "rtl";
  /** Pricing region preselected when this language is chosen. */
  defaultRegion: string | null;
}

export interface RegionPrice {
  id: string;
  flag: string;
  country: string;
  currency: string;
  monthly: string;
  yearly: string;
  /** Yearly price expressed per month, e.g. "≈ $10.00". */
  perMonth: string;
  /** Percentage saved by paying yearly. */
  save: number;
}

export interface ToneLane {
  code: string;
  label: string;
  sample: string;
}

export interface TitledItem {
  title: string;
  text?: string;
}

export interface CtaContent {
  label: string;
  action: LandingAction;
}

export interface PlanTier {
  kicker: string;
  /** Present only on the highlighted (Pro) tier. */
  badge?: string;
  name: string;
  tagline: string;
  features: string[];
  cta: CtaContent;
}

export interface ProfileField {
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

export type LandingAction = "start_map" | "start_free" | "start_pro" | "learn_more";

export const DEFAULT_LANGUAGE: LandingLangCode = "en";
export const DEFAULT_LANE = "UK";
export const DEFAULT_PRICING_REGION = "US";

export const LANGUAGES: readonly LandingLanguage[] = [
  {
    "code": "en",
    "nativeName": "English",
    "englishName": "English",
    "dir": "ltr",
    "defaultRegion": "US"
  },
  {
    "code": "th",
    "nativeName": "ไทย",
    "englishName": "Thai",
    "dir": "ltr",
    "defaultRegion": "TH"
  },
  {
    "code": "vi",
    "nativeName": "Tiếng Việt",
    "englishName": "Vietnamese",
    "dir": "ltr",
    "defaultRegion": "VN"
  },
  {
    "code": "id",
    "nativeName": "Bahasa Indonesia",
    "englishName": "Indonesian",
    "dir": "ltr",
    "defaultRegion": "ID"
  },
  {
    "code": "ms",
    "nativeName": "Bahasa Melayu",
    "englishName": "Malay",
    "dir": "ltr",
    "defaultRegion": "MY"
  },
  {
    "code": "fil",
    "nativeName": "Filipino",
    "englishName": "Filipino",
    "dir": "ltr",
    "defaultRegion": "PH"
  },
  {
    "code": "ko",
    "nativeName": "한국어",
    "englishName": "Korean",
    "dir": "ltr",
    "defaultRegion": "KR"
  },
  {
    "code": "ja",
    "nativeName": "日本語",
    "englishName": "Japanese",
    "dir": "ltr",
    "defaultRegion": "JP"
  },
  {
    "code": "zh-CN",
    "nativeName": "中文",
    "englishName": "Chinese (Simplified)",
    "dir": "ltr",
    "defaultRegion": "SG"
  },
  {
    "code": "hi",
    "nativeName": "हिन्दी",
    "englishName": "Hindi",
    "dir": "ltr",
    "defaultRegion": "IN"
  },
  {
    "code": "ar",
    "nativeName": "العربية",
    "englishName": "Arabic",
    "dir": "rtl",
    "defaultRegion": "GCC"
  },
  {
    "code": "es",
    "nativeName": "Español",
    "englishName": "Spanish",
    "dir": "ltr",
    "defaultRegion": "ES"
  },
  {
    "code": "pt-BR",
    "nativeName": "Português",
    "englishName": "Portuguese",
    "dir": "ltr",
    "defaultRegion": "BR"
  },
  {
    "code": "fr",
    "nativeName": "Français",
    "englishName": "French",
    "dir": "ltr",
    "defaultRegion": null
  },
  {
    "code": "de",
    "nativeName": "Deutsch",
    "englishName": "German",
    "dir": "ltr",
    "defaultRegion": null
  }
];

/** Tone lane previewed when a language is chosen. */
export const LANG_TO_LANE: Readonly<Record<string, string>> = {
  "en": "UK",
  "th": "TH",
  "ja": "JP",
  "ko": "KR",
  "pt-BR": "BR",
  "es": "ES",
  "hi": "IN",
  "ar": "GCC",
  "ms": "MY",
  "zh-CN": "SG"
};

/** Display prices per country. Marketing copy only — checkout prices come from the billing backend. */
export const REGION_PRICES: readonly RegionPrice[] = [
  {
    "id": "US",
    "flag": "🇺🇸",
    "country": "United States",
    "currency": "USD",
    "monthly": "$14.99",
    "yearly": "$119.99",
    "perMonth": "≈ $10.00",
    "save": 33
  },
  {
    "id": "UK",
    "flag": "🇬🇧",
    "country": "United Kingdom",
    "currency": "GBP",
    "monthly": "£11.99",
    "yearly": "£99.99",
    "perMonth": "≈ £8.33",
    "save": 31
  },
  {
    "id": "CA",
    "flag": "🇨🇦",
    "country": "Canada",
    "currency": "CAD",
    "monthly": "C$12.99",
    "yearly": "C$109.99",
    "perMonth": "≈ C$9.17",
    "save": 29
  },
  {
    "id": "TH",
    "flag": "🇹🇭",
    "country": "Thailand",
    "currency": "THB",
    "monthly": "฿299",
    "yearly": "฿2,490",
    "perMonth": "≈ ฿208",
    "save": 31
  },
  {
    "id": "VN",
    "flag": "🇻🇳",
    "country": "Vietnam",
    "currency": "VND",
    "monthly": "149.000₫",
    "yearly": "1.249.000₫",
    "perMonth": "≈ 104.000₫",
    "save": 30
  },
  {
    "id": "ID",
    "flag": "🇮🇩",
    "country": "Indonesia",
    "currency": "IDR",
    "monthly": "Rp 79.000",
    "yearly": "Rp 649.000",
    "perMonth": "≈ Rp 54.000",
    "save": 32
  },
  {
    "id": "PH",
    "flag": "🇵🇭",
    "country": "Philippines",
    "currency": "PHP",
    "monthly": "₱249",
    "yearly": "₱1,990",
    "perMonth": "≈ ₱166",
    "save": 33
  },
  {
    "id": "MY",
    "flag": "🇲🇾",
    "country": "Malaysia",
    "currency": "MYR",
    "monthly": "RM19",
    "yearly": "RM159",
    "perMonth": "≈ RM13.25",
    "save": 30
  },
  {
    "id": "SG",
    "flag": "🇸🇬",
    "country": "Singapore",
    "currency": "SGD",
    "monthly": "S$7.99",
    "yearly": "S$64.99",
    "perMonth": "≈ S$5.42",
    "save": 32
  },
  {
    "id": "KR",
    "flag": "🇰🇷",
    "country": "Korea",
    "currency": "KRW",
    "monthly": "₩7,900",
    "yearly": "₩64,900",
    "perMonth": "≈ ₩5,408",
    "save": 32
  },
  {
    "id": "JP",
    "flag": "🇯🇵",
    "country": "Japan",
    "currency": "JPY",
    "monthly": "¥900",
    "yearly": "¥7,500",
    "perMonth": "≈ ¥625",
    "save": 31
  },
  {
    "id": "IN",
    "flag": "🇮🇳",
    "country": "India",
    "currency": "INR",
    "monthly": "₹299",
    "yearly": "₹2,499",
    "perMonth": "≈ ₹208",
    "save": 30
  },
  {
    "id": "GCC",
    "flag": "🌍",
    "country": "GCC (USD)",
    "currency": "USD",
    "monthly": "$12.99",
    "yearly": "$109.99",
    "perMonth": "≈ $9.17",
    "save": 29
  },
  {
    "id": "ES",
    "flag": "🇪🇸",
    "country": "Spain",
    "currency": "EUR",
    "monthly": "7,99 €",
    "yearly": "64,99 €",
    "perMonth": "≈ 5,42 €",
    "save": 32
  },
  {
    "id": "BR",
    "flag": "🇧🇷",
    "country": "Brazil",
    "currency": "BRL",
    "monthly": "R$ 24",
    "yearly": "R$ 199",
    "perMonth": "≈ R$ 16,58",
    "save": 31
  },
  {
    "id": "MX",
    "flag": "🇲🇽",
    "country": "Mexico",
    "currency": "MXN",
    "monthly": "MXN $129",
    "yearly": "MXN $1,049",
    "perMonth": "≈ MXN $87",
    "save": 32
  },
  {
    "id": "AR",
    "flag": "🇦🇷",
    "country": "Argentina",
    "currency": "ARS",
    "monthly": "ARS $6.900",
    "yearly": "ARS $56.900",
    "perMonth": "≈ ARS $4.742",
    "save": 31
  },
  {
    "id": "CO",
    "flag": "🇨🇴",
    "country": "Colombia",
    "currency": "COP",
    "monthly": "COP $24.900",
    "yearly": "COP $199.900",
    "perMonth": "≈ COP $16.658",
    "save": 33
  },
  {
    "id": "CL",
    "flag": "🇨🇱",
    "country": "Chile",
    "currency": "CLP",
    "monthly": "CLP $6.900",
    "yearly": "CLP $56.900",
    "perMonth": "≈ CLP $4.742",
    "save": 31
  },
  {
    "id": "PE",
    "flag": "🇵🇪",
    "country": "Peru",
    "currency": "PEN",
    "monthly": "S/24",
    "yearly": "S/199",
    "perMonth": "≈ S/16.58",
    "save": 31
  }
];

export const CORE_PRINCIPLES: readonly string[] = [
  "Signals over symbolism",
  "Rhythm over prediction",
  "Mapping over interpretation",
  "Direction over destiny"
];

export const TONE_LANES: readonly ToneLane[] = [
  {
    "code": "UK",
    "label": "Clarity",
    "sample": "Pace is slowing and clarity is rising. Pick one priority and finish it before noon."
  },
  {
    "code": "TH",
    "label": "Soft Direct",
    "sample": "Your pace is gently slowing today. It may help to finish one important thing first — then let yourself rest."
  },
  {
    "code": "JP",
    "label": "Precision",
    "sample": "Pace: easing. Clarity: rising. Recommended: one focused task, 09:00–11:00. Keep the afternoon light."
  },
  {
    "code": "KR",
    "label": "Calm Structure",
    "sample": "Today moves slower, and that is fine. Morning: one key task. Afternoon: review. Evening: rest."
  },
  {
    "code": "BR",
    "label": "Warm Direct",
    "sample": "Hey — your rhythm is asking for a slower day. Do the one thing that matters, then be kind to yourself."
  },
  {
    "code": "ES",
    "label": "Emotive Clarity",
    "sample": "Something in you wants to slow down — listen to it. Clarity is arriving; give it room with one clear choice."
  },
  {
    "code": "IN",
    "label": "Reflective",
    "sample": "A quieter rhythm today. Before acting, ask what truly needs your attention — the answer will come more easily than usual."
  },
  {
    "code": "GCC",
    "label": "Formal Soft",
    "sample": "Your rhythm today suggests a measured pace. We recommend completing one priority with care and reserving the evening for rest."
  },
  {
    "code": "SG",
    "label": "Neutral Precision",
    "sample": "Pace lower, clarity higher. Focus on one priority this morning; keep the afternoon light."
  },
  {
    "code": "MY",
    "label": "Neutral Precision",
    "sample": "Pace lower, clarity higher. Focus on one priority this morning; keep the afternoon light."
  }
];
export const LANE_LOGIC: string = "Guidance adapts to tone, pacing, and cultural communication norms of each lane.";
export const LANE_DEMO_CONTEXT: string = "Same moment · your tone";
export const LIFESTYLE_COVERAGE: readonly string[] = [
  "Relationships",
  "Family",
  "Work",
  "Money",
  "Health",
  "Decision-making",
  "Personal growth"
];

export const HERO = {
  "eyebrow": "Global Life GPS · 15 languages",
  "headline": "Astria — Astrology × Life GPS",
  "subheadline": "Clarity in where you are. Direction for where you're going.",
  "body": "Astria turns celestial rhythm into a Life GPS — adapted to your language, tone and worldview.",
  "ctaPrimary": {
    "label": "Get your map",
    "action": "start_map"
  },
  "ctaSecondary": {
    "label": "How it works",
    "action": "learn_more"
  }
} as const;

export const UNIQUE_MAP = {
  "eyebrow": "Your map",
  "body": "Your rhythm, your pace, your clarity — Astria builds a Life GPS that moves with you.",
  "items": [
    {
      "title": "Birth rhythm profile",
      "text": "The timing and place you began — the baseline of your rhythm."
    },
    {
      "title": "Core rhythm guidance",
      "text": "How you naturally move, decide and recover."
    },
    {
      "title": "Life navigation map",
      "text": "Where you are now, and where your rhythm is pointing next."
    }
  ]
};
export const PERSONAL_GUIDANCE = {
  "eyebrow": "Your lane",
  "body": "Astria reads your tone and rhythm, then shapes guidance to your lane — the communication style that feels natural where you are.",
  "items": [
    "Your rhythm, plan and guidance",
    "Your pace and timing",
    "Your map, your step"
  ]
};
export const LIFE_NAVIGATION = {
  "eyebrow": "How Astria thinks",
  "body": "Astrology is the language — rhythm, signals and mapping are the engine.",
  "pillars": [
    {
      "title": "Signals",
      "text": "Planetary timing, read as signals — not omens."
    },
    {
      "title": "Mapping",
      "text": "Signals placed on a map of your life."
    },
    {
      "title": "Guidance",
      "text": "Clear next steps, in a tone that fits you."
    },
    {
      "title": "Language",
      "text": "Your language and worldview, not a translation."
    }
  ]
};
export const CALM_GUIDANCE = {
  "moments": [
    {
      "label": "☀ Morning brief",
      "text": "A 30-second read of today's rhythm before the day begins."
    },
    {
      "label": "◐ Timing nudge",
      "text": "A quiet heads-up when a good window opens."
    },
    {
      "label": "☾ Evening reflection",
      "text": "One question to close the day with clarity."
    }
  ]
};
export const INNER_WORLD = {
  "eyebrow": "Life GPS metrics",
  "body": "Astria tracks how your clarity, pace, stability, emotional tone and direction shift over time.",
  "metrics": [
    {
      "label": "Clarity",
      "sampleValue": "Rising",
      "description": "How clearly you can see your inner and outer world."
    },
    {
      "label": "Pace",
      "sampleValue": "Easing",
      "description": "How fast or slow your rhythm is moving today."
    },
    {
      "label": "Stability",
      "sampleValue": "Steady",
      "description": "How steady your emotional and mental ground feels."
    },
    {
      "label": "Emotional tone",
      "sampleValue": "Warm",
      "description": "The underlying emotional color of your rhythm."
    },
    {
      "label": "Direction",
      "sampleValue": "Forward",
      "description": "Where your rhythm is naturally pointing you next."
    }
  ]
};
export const STEPS_TO_CLARITY = {
  "eyebrow": "How it works",
  "steps": [
    {
      "number": "01",
      "title": "Birth rhythm",
      "text": "Share when and where you were born."
    },
    {
      "number": "02",
      "title": "Core rhythm guidance",
      "text": "Understand how you naturally move."
    },
    {
      "number": "03",
      "title": "Life navigation map",
      "text": "See where you are and where you're heading."
    },
    {
      "number": "04",
      "title": "Integration",
      "text": "Daily guidance that fits into real life."
    }
  ]
};
export const PROFILE: {
  eyebrow: string;
  body: string;
  fields: ProfileField[];
  cta: CtaContent;
  successMessage: string;
  note: string;
} = {
  "eyebrow": "Start here",
  "body": "Four details are all it takes. Your map is private, and you can edit it anytime.",
  "fields": [
    {
      "label": "Name",
      "type": "text",
      "required": true,
      "placeholder": "Your first name"
    },
    {
      "label": "Date of birth",
      "type": "date",
      "required": true
    },
    {
      "label": "Time of birth",
      "type": "time",
      "required": false
    },
    {
      "label": "Location",
      "type": "text",
      "required": true,
      "placeholder": "City of birth"
    }
  ],
  "cta": {
    "label": "Create profile",
    "action": "start_map"
  },
  "successMessage": "✓ Profile created — your map is on its way",
  "note": "No birth time? That's fine — you can add it later."
};
export const DEEP_UNDERSTANDING = {
  "title": "One step, deeper understanding.",
  "items": [
    {
      "title": "Signals integration",
      "text": "Your timing, combined into one clear signal."
    },
    {
      "title": "Rhythm mapping",
      "text": "Your cycles placed on a readable map."
    },
    {
      "title": "Guidance alignment",
      "text": "Advice tuned to your lane and language."
    }
  ]
};
export const DOMAINS = {
  "eyebrow": "Focus",
  "items": [
    {
      "title": "Relationships",
      "text": "Timing for honest conversations, and understanding how you connect."
    },
    {
      "title": "Career",
      "text": "Windows for focus, visibility and decisions that move you forward."
    },
    {
      "title": "Inner world",
      "text": "Noticing your pace and emotional tone, so rest is never guesswork."
    }
  ]
};
export const GLOBAL_LANGUAGE = {
  "eyebrow": "Global by design",
  "items": [
    {
      "title": "Core language",
      "text": "The interface and your map, in your own language."
    },
    {
      "title": "Guidance language",
      "text": "Advice written in the tone of your lane."
    },
    {
      "title": "Worldview framing",
      "text": "Ideas framed in a way that makes sense where you live."
    }
  ],
  "footnote": "Adapted to cultural worldview — not spiritual tags, not zodiac logic, not destiny framing."
};
export const PRIVACY = {
  "eyebrow": "Trust",
  "items": [
    {
      "title": "Privacy by design",
      "text": "Birth data is encrypted and never sold. Delete it anytime."
    },
    {
      "title": "Careful signal language",
      "text": "No fear, no fate — guidance that leaves the choice with you."
    },
    {
      "title": "Crafted for clarity",
      "text": "Short, calm and readable. Nothing to decode."
    }
  ]
};
export const PLANS: {
  eyebrow: string;
  body: string;
  fineprint: string;
  tiers: PlanTier[];
} = {
  "eyebrow": "Plans",
  "body": "Local pricing in 20 countries.",
  "fineprint": "7-day free trial · Cancel anytime · Prices in local currency",
  "tiers": [
    {
      "kicker": "Free",
      "name": "Start your rhythm",
      "tagline": "Everything you need to begin.",
      "features": [
        "Basic rhythm scan",
        "Daily rhythm insights",
        "Limited guidance",
        "All 15 languages"
      ],
      "cta": {
        "label": "Get started",
        "action": "start_free"
      }
    },
    {
      "kicker": "Pro · Your Life GPS",
      "badge": "Most loved",
      "name": "Your full Life GPS",
      "tagline": "The complete navigation system for your life.",
      "features": [
        "Full rhythm engine",
        "Advanced mapping",
        "Unlimited guidance",
        "Confidential insights"
      ],
      "cta": {
        "label": "Unlock Your Life GPS",
        "action": "start_pro"
      }
    }
  ]
};
export const UNDERSTAND_WORLD = {
  "items": [
    "See",
    "Read",
    "Think",
    "Say",
    "Move",
    "Reflect"
  ]
};
export const FAQ = {
  "eyebrow": "Questions",
  "items": [
    {
      "q": "Does Astria predict the future?",
      "a": "No. Astria reads rhythm, not destiny. It shows your current pace, clarity and direction so you can choose your next step with more confidence."
    },
    {
      "q": "What if I don't know my birth time?",
      "a": "You can still create your map. Some details will be broader, and you can add your time whenever you find it."
    },
    {
      "q": "Can I use Astria in my language?",
      "a": "Yes. Astria works in 15 languages, and guidance adapts to your lane — the tone that feels natural where you live."
    },
    {
      "q": "Can I cancel anytime?",
      "a": "Always. Cancel in a tap and keep access until the end of your billing period."
    }
  ]
};
export const FINAL_CTA = {
  "eyebrow": "Your Life GPS",
  "cta": {
    "label": "Get started",
    "action": "start_map"
  }
};
export const FOOTER_NOTE: string = "© 2026 Astria · For reflection and self-discovery, not professional advice.";
